import { clamp } from "lodash-es";
import { colors } from "widgets/utils";

import { SyntenyTypeEnum, TrackEnum } from "../../openapi";
import { GROUP_HEADER_HEIGHT } from "../../settings.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { SelectionRange, Track, TrackGroups, Tracks } from "../../types.ts";
import { drawHover } from "./hover-renderer.ts";
import { rangeSelectionBarRenderer } from "./range-selection-bar-renderer.ts";
import { rangeSelectionRenderer } from "./range-selection-renderer.ts";

/**
 * @param tracks all tracks
 * @param baseShift scrollX in base to prevent approximation of pixels
 */
export function drawTrackImageBitmaps(tracks: Track[], baseShift: number) {
  const { ctx, activeSyntenyType, basePerPixel, scrollX } = useGenomeViewerStore.getState();
  let currentY = GROUP_HEADER_HEIGHT;

  if (!ctx) return;

  for (const { imageBitmap, height, baseStart, basePerPixel: trackBasePerPixel, trackType } of tracks) {
    const sliceEndY = currentY + height;
    ctx.save();
    if (
      sliceEndY > 0 &&
      currentY < ctx.canvas.height &&
      imageBitmap &&
      baseStart !== undefined &&
      trackBasePerPixel !== undefined
    ) {
      if (
        trackType === TrackEnum.SEQUENCE &&
        Math.floor(1 / trackBasePerPixel) < 1 &&
        activeSyntenyType != SyntenyTypeEnum.GENOME_ALIGNMENT
      ) {
        ctx.translate(0, currentY);
        // If not zoomed enough to see sequence track
        ctx.font = "12px Roboto Flex";
        ctx.fillStyle = colors.textSecondary;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("Zoom in to see sequences", ctx.canvas.width / 2, height / 2);
      } else if (imageBitmap) {
        // Calculate the scale factor between the track's basePerPixel and the current state's basePerPixel
        const scaleFactor = trackBasePerPixel / basePerPixel;

        // Apply the scaling to both position and image
        const xPosition = (baseShift + baseStart) / basePerPixel - scrollX;
        ctx.translate(xPosition, currentY);

        ctx.scale(scaleFactor, 1); // Scale only horizontally
        // Draw the image bitmap for other track types
        ctx.drawImage(imageBitmap, 0, 0);
      }
    }

    ctx.restore();

    currentY = sliceEndY;
  }
}

export function drawAlignmentImageBitmaps() {
  const {
    ctx,
    alignmentImageBitmapBasePerPixel,
    alignmentImageBitmap,
    alignmentImageBitmapScrollX,
    alignmentImageBitmapScrollY,
    basePerPixel,
    scrollX,
    scrollY,
  } = useGenomeViewerStore.getState();

  if (!ctx) return;

  const scaleFactor = alignmentImageBitmapBasePerPixel / basePerPixel;

  if (alignmentImageBitmap) {
    ctx.save();

    const xPosition = scaleFactor * alignmentImageBitmapScrollX - scrollX;

    ctx.translate(xPosition, alignmentImageBitmapScrollY - scrollY);
    ctx.scale(scaleFactor, 1); // Scale only horizontally
    ctx.drawImage(alignmentImageBitmap, 0, 0);
    ctx.restore();
  }
}

export function drawTrackShiftBackground(shiftX: number, height: number, length: number) {
  const state = useGenomeViewerStore.getState();
  const ctx = state.ctx!;

  // At the beginning of the track
  if (state.scrollX < shiftX) {
    ctx.save();
    ctx.fillStyle = colors.disabledBackground;
    ctx.fillRect(0, 0, shiftX - state.scrollX, height);
    ctx.restore();
  }

  // At the end of the track
  if (state.scrollX + ctx.canvas.width > shiftX + length / state.basePerPixel) {
    ctx.save();
    ctx.fillStyle = colors.disabledBackground;
    ctx.fillRect(
      state.scrollX > shiftX + length / state.basePerPixel ? 0 : shiftX + length / state.basePerPixel - state.scrollX,
      0,
      ctx.canvas.width,
      height,
    );
    ctx.restore();
  }
}

export function drawHoveredFeatures() {
  const state = useGenomeViewerStore.getState();
  if (!state.isDragging && state.hoverData != undefined) {
    // find orthologs friends
    const friends = (
      state.orthologousGroups.find((group) => group.orthologId === state.hoverData?.orthologId)?.orthologs ?? []
    ).filter((f) => f.id !== state.hoverData?.id);

    for (const friend of friends) {
      const track = state.tracks.get(friend.trackId);
      if (track?.isVisible) {
        const trackGroup = state.trackGroups.get(track.groupId);
        if (trackGroup?.graphics.isVisible) {
          drawHover(
            { ...friend, x: friend.x - state.scrollX + trackGroup.graphics.baseShift / state.basePerPixel },
            true,
          );
        }
      }
    }
    drawHover(state.hoverData);
  }
}

export function drawRangeSelectionText(x: number, text: string) {
  const { ctx } = useGenomeViewerStore.getState();
  if (ctx && text) {
    const textWidth = ctx.measureText(text).width;

    ctx.fillStyle = colors.panelBackground;
    ctx.fillRect(x - textWidth / 2 - 4, 1, textWidth + 8, 11 + 4); // Because 11px font and +4 for vertical padding

    ctx.fillStyle = colors.selectStroke;
    ctx.fillText(text, x - textWidth / 2, GROUP_HEADER_HEIGHT / 2 + 3);
  }
}

export function drawRangeSelection(
  ctx: CanvasRenderingContext2D,
  selectedRange: SelectionRange,
  trackGroups: TrackGroups,
  tracks: Tracks,
  trackGroupsInSelection: string[],
  basePerPixel: number,
  scrollX: number,
  scrollY: number,
) {
  if (selectedRange) {
    // Not `RobotoMono` in the main thread as we use the application web font
    ctx.font = "11px Roboto Flex Mono";

    const rangeStart = Math.min(selectedRange.start, selectedRange.stop);
    const rangeStop = Math.max(selectedRange.start, selectedRange.stop);

    const rangeLeftX = rangeStart / basePerPixel - scrollX;
    const rangeRightX = rangeStop / basePerPixel - scrollX;

    let offsetY = 0;

    // Draw the selected range relative coordinates
    for (const [groupId, trackGroup] of [...trackGroups.entries()].filter(([_, group]) => group.graphics.isVisible)) {
      const visibleTracks = trackGroup.trackIds
        .map((trackId) => tracks.get(trackId))
        .filter((track): track is NonNullable<typeof track> => !!track?.isVisible);

      ctx.save();
      ctx.translate(0, Math.floor(offsetY - scrollY));
      const height = [...visibleTracks.values()].reduce((acc, track) => acc + track.height, GROUP_HEADER_HEIGHT);

      if (trackGroupsInSelection.includes(groupId)) {
        rangeSelectionRenderer(height);
      }

      offsetY += height;
      ctx.restore();
    }

    // Draw range selection drag bars
    rangeSelectionBarRenderer();

    offsetY = 0;
    for (const [groupId, trackGroup] of [...trackGroups.entries()].filter(([_, group]) => group.graphics.isVisible)) {
      const visibleTracks = trackGroup.trackIds
        .map((trackId) => tracks.get(trackId))
        .filter((track): track is NonNullable<typeof track> => !!track?.isVisible);

      ctx.save();
      ctx.translate(0, Math.floor(offsetY - scrollY));
      const height = [...visibleTracks.values()].reduce((acc, track) => acc + track.height, GROUP_HEADER_HEIGHT);

      if (trackGroupsInSelection.includes(groupId))
        if (offsetY - scrollY + height > 0 && offsetY - scrollY < ctx.canvas.height) {
          const textLeftX = clamp(
            Math.floor(rangeStart - trackGroup.graphics.baseShift),
            0,
            trackGroup.graphics.length,
          );
          const textRightX = clamp(
            Math.floor(rangeStop - trackGroup.graphics.baseShift),
            0,
            trackGroup.graphics.length,
          );

          const textLength =
            textLeftX === textRightX ? "No data" : Math.abs(textRightX - textLeftX).toLocaleString("en-US") + " bp";

          const textLengthX = (clamp(rangeLeftX, 0, ctx.canvas.width) + clamp(rangeRightX, 0, ctx.canvas.width)) / 2;

          const canDisplayCoordinates =
            // + 12 to set padding between texts
            // Ensure that the length text isn't overlapping with left coordinates
            textLengthX - ctx.measureText(textLength).width / 2 - 6 >
              rangeLeftX + ctx.measureText(textLeftX.toLocaleString("en-US")).width / 2 + 6 &&
            // Ensure that the length text isn't overlapping with right coordinates
            textLengthX + ctx.measureText(textLength).width / 2 + 6 <
              rangeRightX - ctx.measureText(textLeftX.toLocaleString("en-US")).width / 2 - 6;

          const canDisplayLength =
            (rangeLeftX >= 0 && rangeLeftX <= ctx.canvas.width) ||
            (rangeRightX >= 0 && rangeRightX <= ctx.canvas.width) ||
            (scrollX >= rangeStart / basePerPixel && scrollX + ctx.canvas.width < rangeStop / basePerPixel);

          if (canDisplayCoordinates && canDisplayLength && textLeftX !== textRightX) {
            // Left coordinate of the range, relatively to the current trackGroup
            drawRangeSelectionText(rangeLeftX, (textLeftX + 1).toLocaleString("en-US"));
            // Right coordinate of the range, relatively to the current trackGroup
            drawRangeSelectionText(rangeRightX, (textRightX || 1).toLocaleString("en-US"));
          }

          if (canDisplayLength) {
            drawRangeSelectionText(textLengthX, textLength);
          }
        }

      offsetY += height;
      ctx.restore();
    }
  }
}

export function drawDarkOverlay(y: number, height: number) {
  const ctx = useGenomeViewerStore.getState().ctx!;

  ctx.save();
  ctx.fillStyle = "rgba(0, 0, 0, 0.08)";
  ctx.fillRect(0, y, ctx.canvas.width, y + height);
  ctx.restore();
}
